/* The station room, mounted at the same persistent boundary as the player.
 *
 * Chat is deliberately polled as full snapshots. A room is small, the API
 * bounds its history, and replacing one ordered list is much harder to get
 * subtly wrong than maintaining a second realtime protocol beside the stream.
 * The fast loop runs only after somebody has opened chat; a listener who never
 * uses the room creates no chat traffic at all.
 *
 * A staff-closed room remains as a read-only full snapshot so later moderation
 * still removes a hidden line everywhere. Once the scheduled set itself ends,
 * the public transcript is cleared; chat is deliberately not an archive.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { api, isAbortError } from '../api';
import { useAccount } from '../account';
import type {
  ChatAccount,
  ChatAction,
  ChatDrink,
  ChatDrop,
  ChatFloorRoom,
  ChatIdentity,
  ChatJoinInput,
  ChatMessage,
  ChatPositionInput,
  ChatPresence,
  ChatRoom,
  ChatSnapshot,
  ChatSpaDoorState,
  ChatTattoo,
  ChatVenueRoom,
  StaffOut,
} from '../types';

export const LIVE_CHAT_PANEL_ID = 'live-chat-panel';

const OPEN_POLL_FALLBACK_MS = 3_000;
const OPEN_POLL_MIN_MS = 2_000;
const OPEN_POLL_MAX_MS = 5_000;
const CLOSED_POLL_MS = 12_000;
const CLOSED_POLL_MAX_MS = 20_000;

export type ChatPhase =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'waiting'
  | 'reconnecting'
  | 'ended'
  | 'off_air'
  | 'failed';

interface ChatView {
  received: boolean;
  phase: ChatPhase;
  /** Last-room metadata stays long enough to render a clear ended state. */
  room: ChatRoom | null;
  /** False when `room` is retained from the last non-null snapshot. */
  active: boolean;
  me: ChatIdentity | null;
  messages: ChatMessage[];
  people: ChatPresence[];
  venueRooms: ChatVenueRoom[];
  account: ChatAccount | null;
  drops: ChatDrop[];
  spaDoor: ChatSpaDoorState;
  serverTime: string | null;
  pollAfterMs: number;
  pollError: unknown | null;
}

const INITIAL_VIEW: ChatView = {
  received: false,
  phase: 'idle',
  room: null,
  active: false,
  me: null,
  messages: [],
  people: [],
  venueRooms: [],
  account: null,
  drops: [],
  spaDoor: { bouncer: null, my_request: null, requests: [] },
  serverTime: null,
  pollAfterMs: OPEN_POLL_FALLBACK_MS,
  pollError: null,
};

export interface ChatValue extends ChatView {
  panelId: string;
  open: boolean;
  /** True while the permanent station room is writable. */
  available: boolean;
  unread: number;
  draft: string;
  joining: boolean;
  sending: boolean;
  waterBusy: boolean;
  relaxBusy: boolean;
  tattooBusy: boolean;
  actionBusy: boolean;
  accountBusy: boolean;
  dropBusy: boolean;
  spaDoorBusy: boolean;
  actionError: unknown | null;
  staff: StaffOut | null;
  hidingMessageIds: ReadonlySet<number>;
  closingRoom: boolean;
  moderationError: unknown | null;
  openChat: () => void;
  closeChat: () => void;
  retry: () => void;
  clearActionError: () => void;
  markRead: () => void;
  preserveUnread: (count: number) => void;
  setDraft: (value: string) => void;
  /** One object rather than a positional list: this was seven arguments before
   *  skin and hair, and nine same-typed strings in a row is a bug waiting for a
   *  reorder. ChatJoinInput already describes the shape the API accepts. */
  join: (profile: ChatJoinInput) => Promise<boolean>;
  send: (body: string) => Promise<boolean>;
  move: (x: number, y: number, floorRoom?: ChatFloorRoom) => Promise<boolean>;
  water: (hasWater: boolean) => Promise<boolean>;
  drink: (drink: ChatDrink) => Promise<boolean>;
  relax: (isRelaxing: boolean) => Promise<boolean>;
  tattoo: (tattoo: ChatTattoo) => Promise<boolean>;
  act: (action: ChatAction) => Promise<boolean>;
  setSpaBouncer: (active: boolean) => Promise<boolean>;
  requestSpaEntry: (message: string) => Promise<boolean>;
  decideSpaEntry: (requestId: string, approved: boolean, message: string) => Promise<boolean>;
  registerAccount: (username: string, password: string) => Promise<boolean>;
  loginAccount: (username: string, password: string) => Promise<boolean>;
  logoutAccount: () => Promise<boolean>;
  claimDrop: (dropId: string) => Promise<boolean>;
  startDrop: () => Promise<boolean>;
  endDrop: (dropId: string) => Promise<boolean>;
  hideMessage: (id: number) => Promise<boolean>;
  closeRoom: () => Promise<boolean>;
}

const ChatContext = createContext<ChatValue | null>(null);

export function useChat(): ChatValue {
  const value = useContext(ChatContext);
  if (!value) {
    throw new Error('useChat() was called outside <ChatProvider>.');
  }
  return value;
}

function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(high, Math.max(low, value));
}

function pollDelay(snapshotMs: number, open: boolean): number {
  const suggested = Number.isFinite(snapshotMs) && snapshotMs > 0
    ? snapshotMs
    : OPEN_POLL_FALLBACK_MS;
  if (open) return clamp(suggested, OPEN_POLL_MIN_MS, OPEN_POLL_MAX_MS);
  return clamp(Math.max(suggested, CLOSED_POLL_MS), CLOSED_POLL_MS, CLOSED_POLL_MAX_MS);
}

function nonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // randomUUID is missing in some older WebViews. The backend validates an
  // actual UUID, so the fallback must keep the version and variant bits rather
  // than merely return a random-looking string.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function phaseFor(room: ChatRoom): ChatPhase {
  return room.writable ? 'ready' : 'waiting';
}

/** Count additions in a full replacement without assuming ids are contiguous. */
function additions(previous: readonly ChatMessage[], next: readonly ChatMessage[]): number {
  const seen = new Set(previous.map((message) => message.id));
  return next.reduce((count, message) => count + (seen.has(message.id) ? 0 : 1), 0);
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const {
    account: siteAccount,
    adopt: adoptRadioAccount,
    register: registerRadioAccount,
    login: loginRadioAccount,
    logout: logoutRadioAccount,
  } = useAccount();
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState<ChatView>(INITIAL_VIEW);
  const [unread, setUnread] = useState(0);
  const [draft, setDraft] = useState('');
  const [joining, setJoining] = useState(false);
  const [sending, setSending] = useState(false);
  const [waterBusy, setWaterBusy] = useState(false);
  const [relaxBusy, setRelaxBusy] = useState(false);
  const [tattooBusy, setTattooBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);
  const [spaDoorBusy, setSpaDoorBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown | null>(null);
  const [staff, setStaff] = useState<StaffOut | null>(null);
  const [hidingMessageIds, setHidingMessageIds] = useState<ReadonlySet<number>>(() => new Set());
  const [closingRoom, setClosingRoom] = useState(false);
  const [moderationError, setModerationError] = useState<unknown | null>(null);

  const viewRef = useRef(view);
  viewRef.current = view;
  const pendingSendRef = useRef<{
    roomId: string;
    body: string;
    clientNonce: string;
  } | null>(null);
  const staffCheckStartedRef = useRef(false);
  const staffCheckCompleteRef = useRef(false);

  const available = Boolean(view.room && view.active && view.room.writable);

  /* Staff already have an HttpOnly session cookie. Probe once, only after chat
     is opened, and keep an ordinary listener's 401 completely invisible. */
  useEffect(() => {
    if (!open || staffCheckStartedRef.current || staffCheckCompleteRef.current) return;
    staffCheckStartedRef.current = true;
    const controller = new AbortController();
    api
      .adminMe(controller.signal)
      .then((identity) => {
        staffCheckCompleteRef.current = true;
        setStaff(identity);
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        // Expected for almost every listener. This is capability discovery,
        // not an error surface and never a reason to disturb public chat. Do
        // not mark it complete: signing in later and reopening should retry.
      })
      .finally(() => {
        staffCheckStartedRef.current = false;
      });
    return () => {
      controller.abort();
      staffCheckStartedRef.current = false;
    };
  }, [open]);

  const applySnapshot = useCallback((snapshot: ChatSnapshot, panelOpen: boolean) => {
    const previous = viewRef.current;
    let next: ChatView;

    if (snapshot.room) {
      const sameRoom = previous.room?.id === snapshot.room.id;
      // Each venue room is its own feed, so walking next door is as much a fresh
      // ledger as the room itself ending -- the previous room's lines never
      // carry across, and the server has already scoped the snapshot for us.
      const sameFloor = previous.me?.floor_room === snapshot.me?.floor_room;
      next = {
        received: true,
        phase: phaseFor(snapshot.room),
        room: snapshot.room,
        active: true,
        me: snapshot.me,
        messages: snapshot.messages,
        people: snapshot.people,
        venueRooms: snapshot.venue_rooms,
        account: snapshot.account,
        drops: snapshot.drops,
        spaDoor: snapshot.spa_door,
        serverTime: snapshot.server_time,
        pollAfterMs: snapshot.poll_after_ms,
        pollError: null,
      };

      if (panelOpen) {
        setUnread(0);
      } else if (sameRoom && sameFloor) {
        const count = additions(previous.messages, snapshot.messages);
        if (count > 0) setUnread((value) => value + count);
      } else {
        // A new room -- the station ending, or a move to another floor room --
        // is a new unread ledger; the previous room's lines do not carry in.
        setUnread(snapshot.messages.length);
      }
    } else if (previous.room) {
      // The scheduled room ended. Keep only enough metadata to explain the
      // state; clearing the lines means a post-set chat never becomes a public
      // in-memory archive or strands content that staff can no longer retract.
      next = {
        ...previous,
        received: true,
        phase: 'ended',
        active: false,
        me: snapshot.me,
        messages: [],
        people: snapshot.people,
        venueRooms: snapshot.venue_rooms,
        account: snapshot.account,
        drops: snapshot.drops,
        spaDoor: snapshot.spa_door,
        serverTime: snapshot.server_time,
        pollAfterMs: snapshot.poll_after_ms,
        pollError: null,
      };
      setUnread(0);
    } else {
      next = {
        received: true,
        phase: 'off_air',
        room: null,
        active: false,
        me: snapshot.me,
        messages: [],
        people: snapshot.people,
        venueRooms: snapshot.venue_rooms,
        account: snapshot.account,
        drops: snapshot.drops,
        spaDoor: snapshot.spa_door,
        serverTime: snapshot.server_time,
        pollAfterMs: snapshot.poll_after_ms,
        pollError: null,
      };
      setUnread(0);
    }

    viewRef.current = next;
    setView(next);
    adoptRadioAccount(snapshot.account);
  }, [adoptRadioAccount]);

  /* One self-scheduling loop. Reopening and explicit retries rebuild it and
     fetch immediately; background tabs stop completely and resume immediately. */
  useEffect(() => {
    if (!started) return;

    let alive = true;
    let timer: number | null = null;
    let inFlight: AbortController | null = null;

    if (!viewRef.current.received) {
      const loading = { ...viewRef.current, phase: 'loading' as const, pollError: null };
      viewRef.current = loading;
      setView(loading);
    }

    const clearTimer = (): void => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = (delay: number): void => {
      clearTimer();
      if (document.hidden) return;
      timer = window.setTimeout(() => {
        timer = null;
        void run();
      }, delay);
    };

    const run = async (): Promise<void> => {
      if (!alive) return;
      clearTimer();
      inFlight?.abort();
      const mine = new AbortController();
      inFlight = mine;

      try {
        const snapshot = await api.getChat(mine.signal, open);
        if (!alive || mine.signal.aborted) return;
        applySnapshot(snapshot, open);
        schedule(pollDelay(snapshot.poll_after_ms, open));
      } catch (error) {
        if (!alive || isAbortError(error)) return;
        const previous = viewRef.current;
        const failed: ChatView = {
          ...previous,
          phase: previous.room && previous.active ? 'reconnecting' : 'failed',
          pollError: error,
        };
        viewRef.current = failed;
        setView(failed);
        schedule(open ? OPEN_POLL_MAX_MS : CLOSED_POLL_MAX_MS);
      } finally {
        if (inFlight === mine) inFlight = null;
      }
    };

    const onVisibility = (): void => {
      if (document.hidden) {
        clearTimer();
        inFlight?.abort();
        return;
      }
      void run();
    };

    const onOnline = (): void => void run();
    const onOffline = (): void => {
      clearTimer();
      inFlight?.abort();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    void run();

    return () => {
      alive = false;
      clearTimer();
      inFlight?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [applySnapshot, open, refreshKey, started]);

  const openChat = useCallback(() => {
    setActionError(null);
    setUnread(0);
    setStarted(true);
    setOpen(true);
  }, []);

  const closeChat = useCallback(() => setOpen(false), []);
  const retry = useCallback(() => {
    setActionError(null);
    setRefreshKey((value) => value + 1);
  }, []);
  const clearActionError = useCallback(() => setActionError(null), []);
  const markRead = useCallback(() => setUnread(0), []);
  const preserveUnread = useCallback((count: number) => {
    if (count > 0) setUnread((value) => Math.max(value, count));
  }, []);

  const join = useCallback(async (profile: ChatJoinInput): Promise<boolean> => {
    const name = profile.display_name.trim();
    if (!name || joining) return false;
    setJoining(true);
    setActionError(null);
    try {
      const identity = await api.joinChat({ ...profile, display_name: name });
      const next = { ...viewRef.current, me: identity };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setJoining(false);
    }
  }, [joining]);

  const send = useCallback(async (body: string): Promise<boolean> => {
    const text = body.trim();
    const room = viewRef.current.room;
    // The room the line is being said in. room.id is the same station id in
    // every floor room, so the optimistic append below also has to check this,
    // or a line sent just before walking out lands in the room next door.
    const floorRoomAtSend = viewRef.current.me?.floor_room;
    if (!text || text.length > 280 || !room || !viewRef.current.active || !room.writable || sending) {
      return false;
    }

    setSending(true);
    setActionError(null);
    try {
      // Keep the idempotency key across an uncertain network failure. If the
      // server committed but its response was lost, pressing Send again must
      // recover that canonical row rather than create a duplicate line.
      const pending = pendingSendRef.current;
      const attempt = pending?.roomId === room.id && pending.body === text
        ? pending
        : { roomId: room.id, body: text, clientNonce: nonce() };
      pendingSendRef.current = attempt;
      const message = await api.sendChatMessage({
        room_id: room.id,
        client_nonce: attempt.clientNonce,
        body: text,
      });
      pendingSendRef.current = null;

      // POST returns the canonical row, so the sender sees it without waiting
      // for the next full snapshot. The following poll remains the authority.
      const current = viewRef.current;
      if (
        current.room?.id === room.id
        && current.me?.floor_room === floorRoomAtSend
        && !current.messages.some((item) => item.id === message.id)
      ) {
        const next = { ...current, messages: [...current.messages, message] };
        viewRef.current = next;
        setView(next);
      }
      setDraft('');
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setSending(false);
    }
  }, [sending]);

  const move = useCallback(async (
    x: number,
    y: number,
    floorRoom?: ChatFloorRoom,
  ): Promise<boolean> => {
    const current = viewRef.current;
    if (!current.me) return false;
    const position: ChatPositionInput = {
      x: Math.min(92, Math.max(8, Math.round(x))),
      y: Math.min(84, Math.max(28, Math.round(y))),
      ...(floorRoom ? { floor_room: floorRoom } : {}),
    };
    const existing = current.people.find((person) => person.tag === current.me?.tag);
    const leavingLounge = Boolean(floorRoom && floorRoom !== 'lounge');
    // Empty the rail while changing rooms rather than briefly showing the room
    // just left. The next poll restores the Sundeck's saved main feed or starts
    // a side room at its fresh-on-entry cursor.
    const changingRoom = Boolean(floorRoom && floorRoom !== current.me.floor_room);
    const optimistic: ChatPresence = existing
      ? { ...existing, ...position, ...(leavingLounge ? { is_relaxing: false } : {}) }
      : {
          name: current.me.display_name,
          ...current.me,
          ...position,
          ...(leavingLounge ? { is_relaxing: false } : {}),
        };
    const next = {
      ...current,
      me: floorRoom
        ? {
            ...current.me,
            floor_room: floorRoom,
            ...(leavingLounge ? { is_relaxing: false } : {}),
          }
        : current.me,
      people: [...current.people.filter((person) => person.tag !== optimistic.tag), optimistic],
      ...(changingRoom ? { messages: [] } : {}),
    };
    viewRef.current = next;
    setView(next);
    if (changingRoom) setUnread(0);

    try {
      const presence = await api.moveChatPresence(position);
      const latest = viewRef.current;
      const confirmed = {
        ...latest,
        me: latest.me
          ? {
              ...latest.me,
              floor_room: presence.floor_room,
              is_relaxing: presence.is_relaxing,
            }
          : null,
        people: [...latest.people.filter((person) => person.tag !== presence.tag), presence],
        // Hold the fresh-room clear: a poll that was in flight during the move
        // may have repainted the room we just left, and it must not linger under
        // the new room until the follow-up poll returns the correct feed.
        ...(changingRoom ? { messages: [] } : {}),
      };
      viewRef.current = confirmed;
      setView(confirmed);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      viewRef.current = current;
      setView(current);
      setActionError(error);
      setRefreshKey((value) => value + 1);
      return false;
    }
  }, []);

  const water = useCallback(async (hasWater: boolean): Promise<boolean> => {
    const current = viewRef.current;
    if (!current.me || waterBusy) return false;
    setWaterBusy(true);
    setActionError(null);
    try {
      const identity = await api.setChatWater({ has_water: hasWater });
      const next = {
        ...viewRef.current,
        me: identity,
        people: viewRef.current.people.map((person) => (
          person.tag === identity.tag
            ? { ...person, has_water: identity.has_water, drink: identity.drink }
            : person
        )),
      };
      viewRef.current = next;
      setView(next);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setWaterBusy(false);
    }
  }, [waterBusy]);

  const drink = useCallback(async (drinkToken: ChatDrink): Promise<boolean> => {
    const current = viewRef.current;
    if (!current.me || waterBusy) return false;
    setWaterBusy(true);
    setActionError(null);
    try {
      const identity = await api.setChatDrink({ drink: drinkToken });
      const next = {
        ...viewRef.current,
        me: identity,
        people: viewRef.current.people.map((person) => (
          person.tag === identity.tag
            ? { ...person, has_water: identity.has_water, drink: identity.drink }
            : person
        )),
      };
      viewRef.current = next;
      setView(next);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setWaterBusy(false);
    }
  }, [waterBusy]);

  const relax = useCallback(async (isRelaxing: boolean): Promise<boolean> => {
    const current = viewRef.current;
    if (!current.me || relaxBusy) return false;
    setRelaxBusy(true);
    setActionError(null);
    try {
      const identity = await api.setChatRelax({ is_relaxing: isRelaxing });
      const next = {
        ...viewRef.current,
        me: identity,
        people: viewRef.current.people.map((person) => (
          person.tag === identity.tag
            ? { ...person, is_relaxing: identity.is_relaxing }
            : person
        )),
      };
      viewRef.current = next;
      setView(next);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setRelaxBusy(false);
    }
  }, [relaxBusy]);

  const tattoo = useCallback(async (tattooToken: ChatTattoo): Promise<boolean> => {
    const current = viewRef.current;
    if (!current.me || tattooBusy) return false;
    setTattooBusy(true);
    setActionError(null);
    try {
      const identity = await api.setChatTattoo({ tattoo: tattooToken });
      const next = {
        ...viewRef.current,
        me: identity,
        people: viewRef.current.people.map((person) => (
          person.tag === identity.tag ? { ...person, tattoo: identity.tattoo } : person
        )),
      };
      viewRef.current = next;
      setView(next);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setTattooBusy(false);
    }
  }, [tattooBusy]);

  const act = useCallback(async (action: ChatAction): Promise<boolean> => {
    const current = viewRef.current;
    if (!current.me || actionBusy) return false;
    setActionBusy(true);
    setActionError(null);
    try {
      const identity = await api.setChatAction({ action });
      const next = {
        ...viewRef.current,
        me: identity,
        people: viewRef.current.people.map((person) => (
          person.tag === identity.tag ? { ...person, action: identity.action } : person
        )),
      };
      viewRef.current = next;
      setView(next);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy]);

  const setSpaBouncer = useCallback(async (active: boolean): Promise<boolean> => {
    if (!viewRef.current.me || spaDoorBusy) return false;
    setSpaDoorBusy(true);
    setActionError(null);
    try {
      const spaDoor = await api.setSpaBouncer({ active });
      const next = { ...viewRef.current, spaDoor };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setSpaDoorBusy(false);
    }
  }, [spaDoorBusy]);

  const requestSpaEntry = useCallback(async (message: string): Promise<boolean> => {
    const clean = message.trim();
    if (!viewRef.current.me || !clean || clean.length > 120 || spaDoorBusy) return false;
    setSpaDoorBusy(true);
    setActionError(null);
    try {
      const spaDoor = await api.requestSpaEntry({ message: clean });
      const next = { ...viewRef.current, spaDoor };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setSpaDoorBusy(false);
    }
  }, [spaDoorBusy]);

  const decideSpaEntry = useCallback(async (
    requestId: string,
    approved: boolean,
    message: string,
  ): Promise<boolean> => {
    const clean = message.trim();
    if (!clean || clean.length > 120 || spaDoorBusy) return false;
    setSpaDoorBusy(true);
    setActionError(null);
    try {
      const spaDoor = await api.decideSpaEntry(requestId, { approved, message: clean });
      const next = { ...viewRef.current, spaDoor };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setSpaDoorBusy(false);
    }
  }, [spaDoorBusy]);

  const registerAccount = useCallback(async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    if (accountBusy) return false;
    setAccountBusy(true);
    setActionError(null);
    try {
      const account = await registerRadioAccount(username, password);
      const next = { ...viewRef.current, account };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setAccountBusy(false);
    }
  }, [accountBusy, registerRadioAccount]);

  const loginAccount = useCallback(async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    if (accountBusy) return false;
    setAccountBusy(true);
    setActionError(null);
    try {
      const account = await loginRadioAccount(username, password);
      const next = { ...viewRef.current, account };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setAccountBusy(false);
    }
  }, [accountBusy, loginRadioAccount]);

  const logoutAccount = useCallback(async (): Promise<boolean> => {
    if (accountBusy) return false;
    setAccountBusy(true);
    setActionError(null);
    try {
      await logoutRadioAccount();
      const current = viewRef.current;
      const next = { ...current, account: null, me: null, people: [] };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setAccountBusy(false);
    }
  }, [accountBusy, logoutRadioAccount]);

  const claimDrop = useCallback(async (dropId: string): Promise<boolean> => {
    if (dropBusy) return false;
    setDropBusy(true);
    setActionError(null);
    try {
      const account = await api.claimChatDrop(dropId);
      adoptRadioAccount(account);
      const next = { ...viewRef.current, account };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setActionError(error);
      return false;
    } finally {
      setDropBusy(false);
    }
  }, [adoptRadioAccount, dropBusy]);

  const startDrop = useCallback(async (): Promise<boolean> => {
    if (!staff || dropBusy) return false;
    setDropBusy(true);
    setModerationError(null);
    try {
      await api.startChatDrop({
        item_slug: 'hgr-soft-launch-tee',
        quantity: 20,
        duration_minutes: 120,
      });
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setModerationError(error);
      return false;
    } finally {
      setDropBusy(false);
    }
  }, [dropBusy, staff]);

  const endDrop = useCallback(async (dropId: string): Promise<boolean> => {
    if (!staff || dropBusy) return false;
    setDropBusy(true);
    setModerationError(null);
    try {
      await api.updateChatDrop(dropId, false);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setModerationError(error);
      return false;
    } finally {
      setDropBusy(false);
    }
  }, [dropBusy, staff]);

  const hideMessage = useCallback(async (id: number): Promise<boolean> => {
    if (!staff || hidingMessageIds.has(id)) return false;
    setModerationError(null);
    setHidingMessageIds((ids) => new Set(ids).add(id));
    try {
      await api.hideChatMessage(id);
      const current = viewRef.current;
      const next = { ...current, messages: current.messages.filter((message) => message.id !== id) };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setModerationError(error);
      return false;
    } finally {
      setHidingMessageIds((ids) => {
        const next = new Set(ids);
        next.delete(id);
        return next;
      });
    }
  }, [hidingMessageIds, staff]);

  const closeRoom = useCallback(async (): Promise<boolean> => {
    const room = viewRef.current.room;
    if (!staff || !room || !room.closable || !viewRef.current.active || closingRoom) return false;
    setClosingRoom(true);
    setModerationError(null);
    try {
      await api.setChatRoomEnabled(room.id, false);
      const current = viewRef.current;
      const next: ChatView = {
        ...current,
        phase: 'ended',
        active: false,
        room: current.room ? { ...current.room, writable: false } : null,
      };
      viewRef.current = next;
      setView(next);
      setRefreshKey((value) => value + 1);
      return true;
    } catch (error) {
      setModerationError(error);
      return false;
    } finally {
      setClosingRoom(false);
    }
  }, [closingRoom, staff]);

  const value = useMemo<ChatValue>(() => ({
    ...view,
    account: siteAccount,
    panelId: LIVE_CHAT_PANEL_ID,
    open,
    available,
    unread,
    draft,
    joining,
    sending,
    waterBusy,
    relaxBusy,
    tattooBusy,
    actionBusy,
    accountBusy,
    dropBusy,
    spaDoorBusy,
    actionError,
    staff,
    hidingMessageIds,
    closingRoom,
    moderationError,
    openChat,
    closeChat,
    retry,
    clearActionError,
    markRead,
    preserveUnread,
    setDraft,
    join,
    send,
    move,
    water,
    drink,
    relax,
    tattoo,
    act,
    setSpaBouncer,
    requestSpaEntry,
    decideSpaEntry,
    registerAccount,
    loginAccount,
    logoutAccount,
    claimDrop,
    startDrop,
    endDrop,
    hideMessage,
    closeRoom,
  }), [
    actionError,
    accountBusy,
    actionBusy,
    act,
    setSpaBouncer,
    requestSpaEntry,
    decideSpaEntry,
    available,
    closeChat,
    closeRoom,
    closingRoom,
    clearActionError,
    draft,
    hideMessage,
    hidingMessageIds,
    join,
    joining,
    markRead,
    moderationError,
    open,
    openChat,
    preserveUnread,
    retry,
    registerAccount,
    loginAccount,
    logoutAccount,
    claimDrop,
    startDrop,
    endDrop,
    dropBusy,
    spaDoorBusy,
    relax,
    relaxBusy,
    tattoo,
    tattooBusy,
    send,
    move,
    sending,
    staff,
    unread,
    water,
    drink,
    waterBusy,
    view,
    siteAccount,
  ]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
